import type { SignupAttributionContext } from "@/lib/attribution/types"

/** Canais intermediários (bot, WhatsApp) — não substituem campanha de aquisição no first touch. */
const INTERMEDIATE_CHANNEL_SOURCES = new Set([
  "chatbot",
  "whatsapp",
  "wa",
  "messenger",
])

export type AcquisitionCampaignFields = Pick<
  SignupAttributionContext,
  "source" | "medium" | "campaign" | "content" | "term" | "gclid" | "fbclid" | "ttclid"
>

export function isIntermediateChannelSource(source: string | null | undefined): boolean {
  const normalized = source?.trim().toLowerCase()
  if (!normalized) return false
  return INTERMEDIATE_CHANNEL_SOURCES.has(normalized)
}

export function hasPaidAcquisitionFields(
  fields: AcquisitionCampaignFields | null | undefined,
): boolean {
  if (!fields) return false

  if (fields.gclid?.trim() || fields.fbclid?.trim() || fields.ttclid?.trim()) {
    return true
  }

  const medium = (fields.medium ?? "").toLowerCase()
  if (
    medium === "cpc" ||
    medium === "ppc" ||
    medium === "paid" ||
    medium.includes("cpc") ||
    medium.includes("paid")
  ) {
    return true
  }

  // source+campaign sozinhos podem ser orgânicos (newsletter, referral, etc.).
  // Sticky acquisition só persiste sinais pagos (click IDs ou medium pago).
  return false
}

/** first touch: campanha de aquisição (ex. Google) prevalece sobre canal intermediário da URL. */
export function applyStickyAcquisitionToFirstTouch<T extends AcquisitionCampaignFields>(
  pageFields: T,
  sticky: AcquisitionCampaignFields | null | undefined,
): T {
  if (!sticky || !hasPaidAcquisitionFields(sticky)) {
    return pageFields
  }

  if (!isIntermediateChannelSource(pageFields.source)) {
    return pageFields
  }

  return {
    ...pageFields,
    source: sticky.source?.trim() ? sticky.source : pageFields.source,
    medium: sticky.medium?.trim() ? sticky.medium : pageFields.medium,
    campaign: sticky.campaign?.trim() ? sticky.campaign : pageFields.campaign,
    content: sticky.content?.trim() ? sticky.content : pageFields.content,
    term: sticky.term?.trim() ? sticky.term : pageFields.term,
    gclid: sticky.gclid?.trim() ? sticky.gclid : pageFields.gclid,
    fbclid: sticky.fbclid?.trim() ? sticky.fbclid : pageFields.fbclid,
    ttclid: sticky.ttclid?.trim() ? sticky.ttclid : pageFields.ttclid,
  }
}
